import { useMemo, useState } from 'react'
import { Sparkles } from 'lucide-react'
import type { AppWorkerClient } from '../../../data/AppWorkerClient'
import type { ServerApiClient } from '../../../data/ServerApiClient'
import { CfoInteriorPage } from './CfoInteriorPage'
import { CfoDepthSection } from './CfoDepthSection'
import { CfoAiRead, CfoControlRow, CfoNativeTable, CfoSegmentedControl } from './CfoPagePrimitives'
import { buildCfoDuckDbOpts } from './cfoDuckDb'
import { CFO_SURFACES } from './cfoTelemetry'
import { useCfoBootstrap } from './useCfoBootstrap'
import type { CfoNavProfile } from './cfoNav'
import type { CfoRuntimeProfile } from './cfoRuntimeProfile'
import { buildCfoInstantCardFromContext } from './analysis/instantCards'

const SURFACE = CFO_SURFACES.explore

export type CfoExploreSurface = 'dashboards' | 'viz' | 'spreadsheet'
type ExploreView = CfoExploreSurface

export type CfoExplorePageProps = {
  appApi: ServerApiClient
  workerClient?: AppWorkerClient | null
  workbookId: string | null
  onNavSelect: (navId: string) => void
  /** Route into the workbook's existing dashboard / viz / spreadsheet surfaces. */
  onOpenSurface?: (surface: CfoExploreSurface) => void
  navProfile?: CfoNavProfile
  runtimeProfile?: CfoRuntimeProfile
}

const SURFACES: Array<{ key: CfoExploreSurface; title: string; body: string; cta: string }> = [
  {
    key: 'dashboards',
    title: 'Dashboards',
    body: 'Arrange charts and KPIs into a one-page board you can share. Start from your workbook’s dashboards.',
    cta: 'Open dashboards',
  },
  {
    key: 'viz',
    title: 'Viz builder',
    body: 'Build a chart from your data with shelves for columns, rows, marks, and filters — Tableau-style.',
    cta: 'Open the viz builder',
  },
  {
    key: 'spreadsheet',
    title: 'Spreadsheet',
    body: 'Drop into a familiar grid with formulas and SQL() to model anything the guided pages don’t cover.',
    cta: 'Open a spreadsheet',
  },
]

const BUILDER_SHELF_ROWS = [
  { shelf: 'Rows', value: 'Service line', note: 'Suggested from revenue prompt' },
  { shelf: 'Columns', value: 'Month', note: 'Last 6 months' },
  { shelf: 'Marks', value: 'Bar', note: 'Compare categories' },
  { shelf: 'Filters', value: 'Current company', note: 'Workbook scoped' },
]

const explorePathContext = (surface: (typeof SURFACES)[number]) => ({
  interaction: 'evidence_click',
  source_component: 'explore_path_card',
  source_tables: ['quickbooks_cfo_explore_surface_catalog'],
  metric_id: `explore_${surface.key}`,
  metric_label: surface.title,
  label: surface.title,
  selection_key: `path:${surface.key}`,
  source_fingerprint: `explore:${surface.key}:path`,
  deterministic_read: `${surface.title} is an Explore entry point for the current workbook.`,
  instant_card_insights: [
    `${surface.title} should start from governed workbook context and only open broader data tools after the user chooses a deeper action.`,
  ],
  instant_card_prescriptive_actions: [
    surface.key === 'viz'
      ? 'Start with the suggested shelf fields, then validate the measure grain before adding more dimensions.'
      : surface.key === 'spreadsheet'
        ? 'Use a spreadsheet when the question needs modeling or formulas beyond the governed CFO page cards.'
        : 'Use a dashboard when the user needs a repeatable board view rather than a one-off analysis.',
  ],
  instant_card_next_steps: [
    {
      kind: 'open_surface',
      label: surface.cta,
      prompt: `Open the ${surface.title} surface.`,
      target: { surface: surface.key },
    },
    {
      kind: 'ask',
      label: 'Draft a starter prompt',
      prompt: `Draft a compact starter prompt for ${surface.title} using only this workbook context.`,
      target: { surface: SURFACE, metricId: `explore_${surface.key}` },
    },
  ],
  instant_card_change_explanations: [
    {
      periodKind: 'current_month',
      label: 'Current month',
      status: 'ready',
      current: surface.title,
      prior: 'No selected artifact',
      change: 'Selected path',
      headline: `${surface.title} is the selected Explore path; the next step is to open the governed surface and bind a specific artifact.`,
      drivers: [{
        id: `${surface.key}:path`,
        label: surface.title,
        value: 'Selected',
        direction: 'flat',
        category: 'explore_path',
        sourceSurface: SURFACE,
        target: { surface: surface.key, metricId: `explore_${surface.key}` },
      }],
      confidence: 'high',
      recommendedAction: surface.cta,
    },
  ],
})

const exploreShelfContext = (row: (typeof BUILDER_SHELF_ROWS)[number]) => {
  const metricId = `explore_shelf_${row.shelf.toLowerCase()}`
  return {
    interaction: 'evidence_click',
    source_component: 'explore_shelf_row',
    source_tables: ['quickbooks_cfo_explore_shelf_preview'],
    metric_id: metricId,
    metric_label: `${row.shelf}: ${row.value}`,
    label: `${row.shelf}: ${row.value}`,
    selection_key: `shelf:${row.shelf.toLowerCase()}`,
    source_fingerprint: `explore:shelf:${row.shelf}:${row.value}`,
    deterministic_read: `${row.value} is currently assigned to the ${row.shelf} shelf. ${row.note}.`,
    instant_card_insights: [
      `${row.shelf} controls the ${row.value} role in the draft visualization; validate it before asking AI to add more context.`,
    ],
    instant_card_prescriptive_actions: [
      row.shelf === 'Rows'
        ? 'Confirm the selected dimension is the right business grain before comparing categories.'
        : row.shelf === 'Columns'
          ? 'Confirm the time grain matches the question before adding more series.'
          : row.shelf === 'Marks'
            ? 'Confirm the mark type makes comparisons readable for the selected field count.'
            : 'Confirm the filter scope before treating the chart as a workbook-wide answer.',
    ],
    instant_card_next_steps: [
      {
        kind: 'open_surface',
        label: 'Open viz builder',
        prompt: 'Open the viz builder with this shelf selection.',
        target: { surface: 'viz', metricId },
      },
      {
        kind: 'drilldown',
        label: 'Inspect field fit',
        prompt: `Inspect whether ${row.value} is the right field for the ${row.shelf} shelf.`,
        target: { surface: SURFACE, metricId },
      },
    ],
    instant_card_change_explanations: [
      {
        periodKind: 'current_month',
        label: 'Current selection',
        status: 'ready',
        current: row.value,
        prior: 'Unassigned',
        change: row.shelf,
        headline: `${row.value} is assigned to ${row.shelf}; the key check is whether that shelf role matches the business question.`,
        drivers: [{
          id: `${metricId}:field`,
          label: row.value,
          value: row.note,
          direction: 'flat',
          category: 'viz_shelf',
          sourceSurface: SURFACE,
          target: { surface: SURFACE, metricId },
        }],
        confidence: 'high',
        recommendedAction: `Validate ${row.value} on the ${row.shelf} shelf before adding model-generated analysis.`,
      },
    ],
  }
}

export function CfoExplorePage({ appApi, workerClient = null, workbookId, onNavSelect, onOpenSurface, navProfile = 'full', runtimeProfile }: CfoExplorePageProps) {
  const duckDbOpts = useMemo(() => buildCfoDuckDbOpts(workbookId), [workbookId])
  const { cacheState, bootstrapLoading } = useCfoBootstrap(appApi, workbookId)
  const [view, setView] = useState<ExploreView>('dashboards')
  const instantCards = useMemo(
    () => [
      ...SURFACES.map((surface) => buildCfoInstantCardFromContext({
        context: {
          pageId: 'explore',
          activeSection: SURFACE,
          asOfLabel: 'Explore',
          period: 'explore',
          comparison: 'current',
          sourceComponent: 'explore_path_card',
          sourceTables: ['quickbooks_cfo_explore_surface_catalog'],
          metricId: `explore_${surface.key}`,
          metricLabel: surface.title,
          selection: explorePathContext(surface),
        } as any,
      })),
      ...BUILDER_SHELF_ROWS.map((row) => {
        const context = exploreShelfContext(row)
        return buildCfoInstantCardFromContext({
          context: {
            pageId: 'explore',
            activeSection: SURFACE,
            asOfLabel: 'Explore',
            period: 'explore',
            comparison: 'current',
            sourceComponent: 'explore_shelf_row',
            sourceTables: ['quickbooks_cfo_explore_shelf_preview'],
            metricId: context.metric_id,
            metricLabel: context.metric_label,
            selection: context,
          } as any,
        })
      }),
    ],
    [],
  )

  return (
    <CfoInteriorPage
      appApi={appApi}
      workerClient={workerClient}
      workbookId={workbookId}
      navId="explore"
      activeSection={SURFACE}
      eyebrow="Share"
      title="Explore (viz &amp; sheets)"
      lede={
        <>
          “Build a chart, a sheet, or a dashboard. <em>Describe what you want and I’ll draft it.</em>”
        </>
      }
      crumb={
        <>
          <b>Explore</b> · Viz &amp; sheets
        </>
      }
      asOfLabel="—"
      cacheState={cacheState}
      pageLoading={bootstrapLoading}
      pageLoadingLabel="Checking CFO analytics readiness…"
      copilotSubtitle="building charts with you"
      copilotPlaceholder="Describe a chart…"
      copilotIntro="You’re on Explore. Tell me what you want to see and I’ll build the chart and wire the fields, or open a sheet or dashboard."
      askPlaceholder="Describe a chart and I’ll build it…"
      suggestedPrompts={['Revenue by service line, last 6 months', 'Plot gross margin trend', 'Build me a one-page dashboard']}
      instantCards={instantCards}
      pageFactsFingerprint="explore:catalog:v1"
      duckDbOpts={duckDbOpts}
      onNavSelect={onNavSelect}
      navProfile={navProfile}
      runtimeProfile={runtimeProfile}
    >
      {({ copilot }) => {
        const openExplorePathCard = (surface: (typeof SURFACES)[number]) => {
          void copilot.sendMessage(`Explain the ${surface.title} Explore path and the next best action.`, {
            workspace_context: explorePathContext(surface),
            selected_metric_id: `explore_${surface.key}`,
          } as any)
        }

        const openShelfRowCard = (row: (typeof BUILDER_SHELF_ROWS)[number]) => {
          const context = exploreShelfContext(row)
          void copilot.sendMessage(`Explain the ${row.shelf} shelf choice and what to check next.`, {
            workspace_context: context,
            selected_metric_id: context.metric_id,
          } as any)
        }

        return (
          <>
          <CfoControlRow>
            <CfoSegmentedControl
              label="Explore view"
              value={view}
              onChange={(value) => setView(value as ExploreView)}
              options={[
                { id: 'dashboards', label: 'Dashboards' },
                { id: 'viz', label: 'Viz builder' },
                { id: 'spreadsheet', label: 'Spreadsheet' },
              ]}
            />
            <button type="button" className="cfo-shell-chip">June workbook</button>
          </CfoControlRow>

          <section className="hero rise">
            <div className="hero-top">
              <div>
                <h1>Explore your data, your way.</h1>
                <p>
                  Start from a template, build a chart from plain English, or open a spreadsheet. Arrange the results
                  into a dashboard — the copilot helps at every step.
                </p>
                <span className="tag">AI-assisted · viz · sheets · dashboards</span>
              </div>
            </div>
            <figure className="figure">
              <div className="fig-head">
                <span className="fnum">Preview</span>
                <span className="ftitle">Dashboard canvas</span>
              </div>
              <svg className="fig-svg" viewBox="0 0 920 280" role="img" aria-label="Preview of a dashboard canvas with a bar tile, a line tile, and a KPI tile">
                <rect x="40" y="14" width="840" height="252" rx="12" fill="var(--canvas, #f7f4ec)" stroke="var(--hair, #e7e3d8)" />
                <rect x="64" y="40" width="400" height="200" rx="9" fill="var(--surface, #fff)" stroke="var(--hair, #e7e3d8)" />
                <line x1="84" y1="212" x2="444" y2="212" stroke="var(--grid, #ece8de)" />
                <rect x="100" y="100" width="46" height="112" rx="3" fill="var(--pos, #0e7c86)" />
                <rect x="166" y="138" width="46" height="74" rx="3" fill="var(--pos, #0e7c86)" opacity="0.8" />
                <rect x="232" y="164" width="46" height="48" rx="3" fill="var(--pos, #0e7c86)" opacity="0.62" />
                <rect x="298" y="182" width="46" height="30" rx="3" fill="var(--pos, #0e7c86)" opacity="0.46" />
                <rect x="364" y="196" width="46" height="16" rx="3" fill="var(--pos, #0e7c86)" opacity="0.34" />
                <rect x="480" y="40" width="376" height="118" rx="9" fill="var(--surface, #fff)" stroke="var(--hair, #e7e3d8)" />
                <path d="M500 120 L568 112 L636 124 L704 104 L772 110 L836 98" fill="none" stroke="var(--brand, #1b4965)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
                <rect x="480" y="172" width="376" height="68" rx="9" fill="var(--surface, #fff)" stroke="var(--hair, #e7e3d8)" />
                <text x="500" y="200" style={{ font: '700 9px var(--font-ui)', fill: 'var(--muted, #868c9a)' }}>CASH ON HAND</text>
                <text x="500" y="228" style={{ font: '700 24px var(--font-ui)', fill: 'var(--ink, #1b2430)' }}>Example</text>
              </svg>
              <CfoAiRead>
                Explore should start from a concrete artifact: a template, recent viz, or builder shelf. The selected control above decides which existing workbook surface opens.
              </CfoAiRead>
            </figure>
          </section>

          <div className="section-title rise">
            <h2 className="ff-guide">Ways to start</h2>
            <span className="meta">pick a path · the copilot does the wiring</span>
          </div>
          <div className="cards">
            {/* FE:C7 — Views: the governed Data Studio. */}
            <article className="card edge good rise" data-testid="explore-views-card">
              <div className="body">
                <div className="grow">
                  <h3>Views</h3>
                  <p>Build a governed view by hand or describe it in plain words, preview it, save it, and link it into Excel as a table or FUSEGRID formulas — even past Excel’s row limit.</p>
                </div>
              </div>
              <div className="actions">
                <a className="btn primary" href={`/views${workbookId ? `?company=${encodeURIComponent(workbookId)}` : ''}`}>Open Views</a>
              </div>
            </article>
            {SURFACES.map((s, idx) => (
              <article className={`card edge ${idx === 0 ? 'good' : 'good'} rise`} key={s.key}>
                <div className="body">
                  <div className="grow">
                    <h3>{s.title}</h3>
                    <p>{s.body}</p>
                  </div>
                </div>
                <div className="actions">
                  <button
                    type="button"
                    className="btn secondary"
                    onClick={() => openExplorePathCard(s)}
                    title={`Explain ${s.title}`}
                  >
                    <Sparkles size={14} aria-hidden="true" />
                    Explain
                  </button>
                  <button
                    type="button"
                    className="btn primary"
                    disabled={!onOpenSurface}
                    onClick={() => onOpenSurface?.(s.key)}
                  >
                    {s.cta}
                  </button>
                </div>
              </article>
            ))}
          </div>

          <CfoDepthSection
            title="The viz builder"
            meta="Native shelf preview for fields, marks, and filters"
            showLabel="Show the builder shelf"
            surface={SURFACE}
            workbookId={workbookId}
          >
            {() => (
              <CfoNativeTable
                rows={BUILDER_SHELF_ROWS}
                rowKey={(row) => row.shelf}
                columns={[
                  {
                    key: 'explain',
                    header: '',
                    className: 'cfo-gl-row-action-head',
                    render: (row) => (
                      <button
                        type="button"
                        className="cfo-gl-row-action"
                        onClick={() => openShelfRowCard(row)}
                        title={`Explain ${row.shelf} shelf`}
                        aria-label={`Explain ${row.shelf} shelf`}
                      >
                        <Sparkles size={14} aria-hidden="true" />
                      </button>
                    ),
                  },
                  { key: 'shelf', header: 'Shelf', render: (row) => row.shelf },
                  { key: 'value', header: 'Field / setting', render: (row) => row.value },
                  { key: 'note', header: 'Why', render: (row) => row.note },
                ]}
              />
            )}
          </CfoDepthSection>
          </>
        )
      }}
    </CfoInteriorPage>
  )
}

export default CfoExplorePage
